import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  weatherData,
  tubeStatusData,
  transactionsData
} from "./widgetsDemoMockData";
import { fetchLondonWeather, fetchTubeStatus } from "./widgetsDemoApi";
import balanceAndPinService from "../../features/balance-and-pin/balanceAndPinService";
import { formattedBalance } from "../../features/balance-and-pin/utils";

const SettingsHeader = ({ title }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="flex items-center bg-[#f8f6f6] p-4 pb-2 justify-between">
      <button
        type="button"
        onClick={() => navigate(`/widgets-demo/preferences${location.search}`)}
        className="text-slate-900 flex size-12 shrink-0 items-center justify-center cursor-pointer"
      >
        <span className="material-symbols-outlined">settings</span>
      </button>
      <h2 className="text-slate-900 text-lg font-bold leading-tight tracking-tight flex-1 text-center pr-12">
        {title}
      </h2>
    </div>
  );
};

const LoadingBanner = ({ label }) => (
  <div className="mx-4 mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
    <span className="inline-flex items-center gap-2">
      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#ec5b13]" />
      {label}
    </span>
  </div>
);

const WeatherPage = ({ weather, loading }) => (
  <div className="relative flex min-h-screen w-full max-w-[430px] mx-auto flex-col bg-[#f8f6f6] overflow-x-hidden shadow-2xl">
    <SettingsHeader title={weather.location} />
    <div className="flex flex-col items-center px-6 pt-10 pb-8 relative">
      {loading ? (
        <div className="z-10 flex flex-col items-center">
          <div className="h-32 w-32 rounded-full bg-gray-200 animate-pulse" />
          <div className="mt-4 h-20 w-28 rounded bg-gray-200 animate-pulse" />
          <div className="mt-3 h-6 w-48 rounded bg-gray-200 animate-pulse" />
          <div className="flex gap-4 mt-3">
            <div className="h-5 w-14 rounded bg-gray-200 animate-pulse" />
            <div className="h-5 w-14 rounded bg-gray-200 animate-pulse" />
          </div>
        </div>
      ) : (
        <div className="z-10 flex flex-col items-center">
          <span
            className="material-symbols-outlined text-[#ec5b13] text-8xl mb-2"
            style={{ fontVariationSettings: "'FILL' 1" }}
          >
            {weather.conditionIcon || "partly_cloudy_day"}
          </span>
          <h1 className="text-slate-900 text-[80px] font-bold leading-none tracking-tighter">
            {weather.temperature}
          </h1>
          <h3 className="text-slate-800 text-2xl font-semibold mt-2">
            {weather.condition}
          </h3>
          <div className="flex gap-4 mt-2">
            <p className="text-[#ec5b13] font-medium">H:{weather.high}</p>
            <p className="text-slate-500 font-medium">L:{weather.low}</p>
          </div>
        </div>
      )}
    </div>
    <div className="px-6 py-4">
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-slate-900 font-bold text-lg">7-Day Forecast</h4>
          {loading ? (
            <div className="h-5 w-8 rounded bg-gray-200 animate-pulse" />
          ) : (
            <span className="material-symbols-outlined text-[#ec5b13]">
              calendar_month
            </span>
          )}
        </div>
        <div className="space-y-6">
          {loading
            ? [0, 1, 2, 3, 4, 5, 6].map((idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="h-4 w-12 rounded bg-gray-200 animate-pulse" />
                  <div className="flex items-center gap-3 flex-1 justify-start">
                    <div className="h-6 w-6 rounded-full bg-gray-200 animate-pulse" />
                    <div className="h-3 w-24 rounded bg-gray-200 animate-pulse" />
                  </div>
                  <div className="flex gap-3 w-16 justify-end">
                    <div className="h-3 w-8 rounded bg-gray-200 animate-pulse" />
                    <div className="h-3 w-8 rounded bg-gray-200 animate-pulse" />
                  </div>
                </div>
              ))
            : (weather.forecast7 || weather.forecast).map((item) => (
                <div key={item.day} className="flex items-center justify-between">
                  <p className="text-slate-700 font-semibold w-12">{item.day}</p>
                  <div className="flex items-center gap-3 flex-1 justify-start">
                    <span
                      className="material-symbols-outlined text-[#ec5b13]"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      {item.icon}
                    </span>
                    <span className="text-slate-500 text-sm">
                      {item.summary}
                    </span>
                  </div>
                  <div className="flex gap-3 w-16 justify-end">
                    <span className="text-slate-900 font-bold">{item.high}</span>
                    <span className="text-slate-400">{item.low}</span>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
    <div className="px-6 py-4">
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-slate-900 font-bold text-lg">Hourly Forecast</h4>
          <span className="material-symbols-outlined text-[#ec5b13]">schedule</span>
        </div>

        {loading ? (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((idx) => (
              <div
                key={idx}
                className="min-w-[72px] h-[88px] rounded-xl bg-gray-200 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {(weather.hourly3h || []).map((item) => (
              <div
                key={item.timeLabel}
                className="min-w-[72px] rounded-xl bg-gray-50 border border-slate-100 p-3 text-center"
              >
                <p className="text-[10px] text-gray-400 font-bold uppercase">
                  {item.timeLabel}
                </p>
                <span
                  className="material-symbols-outlined text-[#ec5b13] text-xl"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  {item.icon}
                </span>
                <p className="text-sm font-bold mt-1">{item.temp}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    <div className="px-6 py-4 grid grid-cols-2 gap-4 mt-6">
      {loading ? (
        <>
          <div className="bg-gray-200/60 p-4 rounded-2xl flex flex-col gap-1 animate-pulse">
            <div className="h-4 w-16 rounded bg-gray-300" />
            <div className="mt-4 h-6 w-24 rounded bg-gray-300" />
          </div>
          <div className="bg-gray-200/60 p-4 rounded-2xl flex flex-col gap-1 animate-pulse">
            <div className="h-4 w-20 rounded bg-gray-300" />
            <div className="mt-4 h-6 w-24 rounded bg-gray-300" />
          </div>
        </>
      ) : (
        <>
          <div className="bg-[#ec5b13]/10 p-4 rounded-2xl flex flex-col gap-1">
            <div className="flex items-center gap-2 text-[#ec5b13]">
              <span className="material-symbols-outlined text-sm">air</span>
              <span className="text-xs font-bold uppercase tracking-wider">
                Wind
              </span>
            </div>
            <p className="text-slate-900 text-lg font-bold">{weather.wind}</p>
          </div>
          <div className="bg-[#ec5b13]/10 p-4 rounded-2xl flex flex-col gap-1">
            <div className="flex items-center gap-2 text-[#ec5b13]">
              <span className="material-symbols-outlined text-sm">
                humidity_mid
              </span>
              <span className="text-xs font-bold uppercase tracking-wider">
                Humidity
              </span>
            </div>
            <p className="text-slate-900 text-lg font-bold">{weather.humidity}</p>
          </div>
        </>
      )}
    </div>
  </div>
);

const TubePage = ({ tube, loading }) => (
  <div className="relative flex min-h-screen w-full max-w-[430px] mx-auto flex-col bg-[#f8f6f6] overflow-x-hidden shadow-2xl">
    <SettingsHeader title="Tube Status" />
    {loading && <LoadingBanner label="Loading live tube status..." />}
    <div className="px-4 pb-2 pt-6">
      {loading ? (
        <>
          <div className="h-6 w-64 rounded bg-gray-200 animate-pulse" />
          <div className="mt-3 h-4 w-48 rounded bg-gray-200 animate-pulse" />
        </>
      ) : (
        <>
          <h3 className="text-slate-900 text-xl font-bold leading-tight tracking-tight">
            Real-time Service Updates
          </h3>
          <p className="text-slate-500 text-sm">
            Last updated: {tube.updatedAt}
          </p>
        </>
      )}
    </div>
    <div className="flex flex-col gap-1 px-2 mt-4">
      {loading
        ? [0, 1, 2].map((idx) => (
            <div
              key={idx}
              className="flex flex-col gap-2 bg-white rounded-xl p-4 shadow-sm border-l-4 mt-2 border-gray-200"
            >
              <div className="flex items-start justify-between w-full gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="bg-gray-200 rounded-lg shrink-0 size-12 animate-pulse" />
                  <div className="flex flex-col flex-1">
                    <div className="h-4 w-44 rounded bg-gray-200 animate-pulse" />
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="h-3 w-24 rounded bg-gray-200 animate-pulse" />
                </div>
              </div>
              <div className="h-3 w-full rounded bg-gray-200 animate-pulse" />
            </div>
          ))
        : tube.lines.map((line) => {
            const isAlert = line.state === "alert";
            return (
              <div
                key={line.name}
                className={`flex flex-col gap-2 bg-white rounded-xl p-4 shadow-sm border-l-4 mt-2 ${
                  isAlert ? "border-[#ec5b13]" : "border-emerald-500"
                }`}
              >
                <div className="flex items-start justify-between w-full gap-4">
                  <div className="flex items-start gap-4">
                    <div
                      className={`text-white flex items-center justify-center rounded-lg shrink-0 size-12 ${
                        isAlert ? "bg-[#ec5b13]" : "bg-emerald-500"
                      }`}
                    >
                      <span className="material-symbols-outlined">
                        {isAlert ? "warning" : "check_circle"}
                      </span>
                    </div>
                    <p className="text-slate-900 text-base font-bold leading-normal">
                      {line.name}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p
                      className={`text-sm font-bold uppercase tracking-wider ${
                        isAlert
                          ? "text-[#ec5b13]"
                          : "text-emerald-600"
                      }`}
                    >
                      {line.status}
                    </p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm font-normal leading-snug w-full">
                  {line.details}
                </p>
              </div>
            );
          })}
    </div>
    <div className="px-4 mt-8">
      {loading ? (
        <>
          <div className="h-4 w-44 rounded bg-gray-200 animate-pulse mb-3" />
          <div className="rounded-xl overflow-hidden h-32 bg-gray-200 animate-pulse" />
        </>
      ) : (
        <>
          <h4 className="text-slate-900 font-bold mb-3">Affected Areas</h4>
          <div className="rounded-xl overflow-hidden h-32 relative">
            <img
              alt="London Tube Map View"
              className="w-full h-full object-cover"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuCbRmF-4KNkEMvRmeogpwIwCHMeelJjXlfWUi20DGNkfneY-7CeIQwZDxLnHmmOJdjmWx_6gTdhCqBoViokOBKW09iqAbPvUtrk3UFfzrWoRbMHICqoxBo2DiID8zCbSNjeYIgVT-yJwK7Lmmo2zRFz-Ig9pOcXf3WZDo0qmQczZ0_fE4MPunQgz8IySf5qneysXBFlm3JS3MeO4lERtBtwXG-3-dxytiiTLJqwdDD7Hqr7AO3vikgDxDqLOY4-8n7-H-cCD3ZTUA_m"
            />
            <div className="absolute inset-0 bg-[#ec5b13]/20" />
          </div>
        </>
      )}
    </div>
  </div>
);

const TransactionsPage = ({ balanceFormatted, balanceLoading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAll, setShowAll] = useState(false);

  const visibleItems = showAll
    ? transactionsData.items
    : transactionsData.items.slice(0, 3);

  return (
    <div className="relative flex min-h-screen w-full max-w-md mx-auto flex-col overflow-x-hidden border-x border-slate-200 bg-[#f8f6f6] shadow-2xl">
    <header className="flex items-center p-6 justify-between">
      <button
        type="button"
        onClick={() => navigate(`/widgets-demo/preferences${location.search}`)}
        className="flex size-12 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-900"
      >
        <span className="material-symbols-outlined text-2xl">settings</span>
      </button>
      <h2 className="text-xl font-extrabold leading-tight tracking-tight flex-1 text-center pr-12">
        Balance
      </h2>
    </header>
    <main className="flex-1 overflow-y-auto">
      <div className="px-6 py-10 flex flex-col items-center justify-center text-center">
        <p className="text-[#ec5b13] font-bold text-lg uppercase tracking-widest mb-2">
          Total Available
        </p>
        {balanceLoading ? (
          <div className="h-14 w-56 bg-gray-200 rounded animate-pulse mb-4" />
        ) : (
          <h1 className="text-slate-900 tracking-tighter text-[56px] font-black leading-none mb-4">
            {balanceFormatted}
          </h1>
        )}
        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">trending_up</span>
          {transactionsData.trend}
        </span>
      </div>
      <div className="px-6 pb-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black tracking-tight">Recent Transactions</h2>
          <button
            type="button"
            onClick={() => setShowAll((prev) => !prev)}
            className="text-[#ec5b13] font-bold text-sm"
          >
            {showAll ? "Show less" : "Show all"}
          </button>
        </div>
        <div className="space-y-4">
          {visibleItems.map((item) => (
            <div
              key={`${item.merchant}-${item.meta}`}
              className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-100 shadow-sm"
            >
              <div
                className={`flex size-14 shrink-0 items-center justify-center rounded-full ${
                  item.positive
                    ? "bg-green-100 text-green-600"
                    : "bg-[#ec5b13]/10 text-[#ec5b13]"
                }`}
              >
                <span className="material-symbols-outlined text-3xl font-bold">
                  {item.icon}
                </span>
              </div>
              <div className="flex flex-1 flex-col justify-center">
                <p className="text-slate-900 text-xl font-extrabold leading-tight">
                  {item.merchant}
                </p>
                <p className="text-slate-500 text-base font-bold">{item.meta}</p>
              </div>
              <p
                className={`text-xl font-black ${
                  item.positive ? "text-green-600" : "text-slate-900"
                }`}
              >
                {item.amount}
              </p>
            </div>
          ))}
        </div>
      </div>
    </main>
  </div>
  );
};

const AllWidgetsPage = ({
  weather,
  tube,
  loadingWeather,
  loadingTube,
  balanceFormatted,
  balanceLoading
}) => {
  const [showAllTubeLines, setShowAllTubeLines] = useState(false);
  const tubeLines = tube?.lines || [];
  const displayedTubeLines = showAllTubeLines
    ? tubeLines
    : tubeLines.slice(0, 3);
  const showTubeToggle = tubeLines.length > 3;

  return (
  <main className="max-w-md mx-auto px-4 py-6 space-y-6">
    <section className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
      {loadingWeather && <LoadingBanner label="Loading latest weather..." />}

      {loadingWeather ? (
        <>
          <div className="flex justify-between items-start">
            <div>
              <div className="h-4 w-56 bg-gray-200 rounded animate-pulse" />
              <div className="flex items-baseline mt-1">
                <div className="h-10 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="ml-2 h-4 w-6 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="mt-2 h-4 w-52 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse" />
          </div>
          <div className="mt-6 grid grid-cols-3 gap-2 border-t border-gray-100 pt-4">
            {[0, 1, 2].map((idx) => (
              <div key={idx} className="text-center">
                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse mx-auto" />
                <div className="mt-2 h-5 w-10 bg-gray-200 rounded animate-pulse mx-auto" />
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-gray-500 font-semibold text-sm uppercase tracking-wider">
                London Weather
              </h2>
              <div className="flex items-baseline mt-1">
                <span className="text-5xl font-extrabold">
                  {weather.temperature}
                </span>
                <span className="text-xl font-medium ml-1">C</span>
              </div>
              <p className="text-gray-600 mt-1 font-medium">
                {weather.condition}
              </p>
            </div>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-yellow-500 text-4xl">
                {weather.conditionIcon || "wb_sunny"}
              </span>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-2 border-t border-gray-100 pt-4">
            {weather.forecast.map((item) => (
              <div key={item.day} className="text-center">
                <p className="text-xs text-gray-400 font-bold uppercase">
                  {item.day}
                </p>
                <p className="font-bold text-lg mt-1">{item.high}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </section>

    <section className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
      {loadingTube && <LoadingBanner label="Loading live tube status..." />}

      <div className="flex items-center justify-between mb-4">
        {loadingTube ? (
          <>
            <div className="h-4 w-44 bg-gray-200 rounded animate-pulse" />
            <div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse" />
          </>
        ) : (
          <>
            <h2 className="text-gray-500 font-semibold text-sm uppercase tracking-wider">
              Tube Status
            </h2>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Live
              </span>
              {showTubeToggle && (
                <button
                  type="button"
                  onClick={() => setShowAllTubeLines((prev) => !prev)}
                  className="text-[#ec5b13] font-bold text-sm"
                >
                  {showAllTubeLines ? "Show less" : "Show all"}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="space-y-3">
        {loadingTube
          ? [0, 1, 2].map((idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
              >
                <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
              </div>
            ))
          : displayedTubeLines.map((line) => (
              <div
                key={line.name}
                className="flex flex-col p-3 bg-gray-50 rounded-xl"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="font-bold text-lg">
                    {line.name.replace(" Line", "")}
                  </span>
                  <span
                    className={`text-sm font-semibold ${
                      line.state === "alert"
                        ? "text-amber-600"
                        : "text-green-600"
                    }`}
                  >
                    {line.status}
                  </span>
                </div>
                <p className="text-gray-500 text-sm mt-1">{line.details}</p>
              </div>
            ))}
      </div>
    </section>

    <section className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
      <div className="mb-8">
        <h2 className="text-gray-500 font-semibold text-sm uppercase tracking-wider mb-2">
          Available Balance
        </h2>
        {balanceLoading ? (
          <div className="h-12 w-56 bg-gray-200 rounded animate-pulse" />
        ) : (
          <p className="text-5xl font-black text-slate-900 tracking-tighter">
            {balanceFormatted}
          </p>
        )}
      </div>
      <div>
        <h3 className="text-gray-400 font-bold text-xs uppercase mb-4">
          Recent Activity
        </h3>
        <div className="space-y-4">
          {transactionsData.items.slice(0, 3).map((item) => (
            <div
              key={`${item.merchant}-all`}
              className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0"
            >
              <div className="flex flex-col">
                <span className="text-xl font-extrabold text-slate-800">
                  {item.merchant}
                </span>
                <span className="text-sm text-gray-500 font-medium">
                  {item.meta}
                </span>
              </div>
              <span
                className={`text-xl font-bold ${
                  item.positive ? "text-green-600" : "text-slate-900"
                }`}
              >
                {item.amount}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  </main>
  );
};

const WidgetsDemoView = ({ activeViews }) => {
  const { view } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const [liveWeather, setLiveWeather] = useState(null);
  const [liveTube, setLiveTube] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(true);
  const [loadingTube, setLoadingTube] = useState(true);
  const isViewValid = ["all", "weather", "tube", "transactions"].includes(view);
  const isViewActive = activeViews.includes(view);

  const shouldFetchBalance =
    serialNumber &&
    (view === "all" || view === "transactions") &&
    isViewValid &&
    isViewActive;

  const [balanceLoading, setBalanceLoading] = useState(Boolean(shouldFetchBalance));
  const [balanceFormatted, setBalanceFormatted] = useState("N/A");

  useEffect(() => {
    if (!isViewValid || !isViewActive) {
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      const [weatherResult, tubeResult] = await Promise.allSettled([
        fetchLondonWeather(),
        fetchTubeStatus()
      ]);

      if (!isMounted) {
        return;
      }

      if (weatherResult.status === "fulfilled") {
        setLiveWeather(weatherResult.value);
      }
      setLoadingWeather(false);

      if (tubeResult.status === "fulfilled") {
        setLiveTube(tubeResult.value);
      }
      setLoadingTube(false);
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [isViewActive, isViewValid]);

  useEffect(() => {
    if (!shouldFetchBalance) {
      setBalanceLoading(false);
      setBalanceFormatted("N/A");
      return;
    }

    let isMounted = true;

    const loadBalance = async () => {
      setBalanceLoading(true);
      try {
        const response = await balanceAndPinService.getBalanceAndPin(
          serialNumber,
          "?provider=much_better"
        );

        const availableBalance = response?.balanceDetails?.availableBalance;
        const currency = response?.balanceDetails?.currency;

        const formatted = formattedBalance(availableBalance, currency);
        const normalized =
          formatted === "N/A" ? "N/A" : formatted.replace("£ ", "£");

        if (isMounted) {
          setBalanceFormatted(normalized);
        }
      } catch (error) {
        if (isMounted) {
          setBalanceFormatted("N/A");
        }
      } finally {
        if (isMounted) {
          setBalanceLoading(false);
        }
      }
    };

    loadBalance();

    return () => {
      isMounted = false;
    };
  }, [serialNumber, shouldFetchBalance]);

  const effectiveWeather = useMemo(() => liveWeather || weatherData, [liveWeather]);
  const effectiveTube = useMemo(() => liveTube || tubeStatusData, [liveTube]);

  if (!isViewValid || !isViewActive) {
    return (
      <Navigate
        to={`/widgets-demo/preferences${location.search || ""}`}
        replace
      />
    );
  }

  return (
    <div
      className="widgets-demo-root bg-[#f8f6f6] min-h-screen text-slate-900"
      style={{ fontFamily: "'Public Sans', sans-serif" }}
    >
      {view === "all" && (
        <div>
          <SettingsHeader title="Dashboard" />
          <AllWidgetsPage
            weather={effectiveWeather}
            tube={effectiveTube}
            loadingWeather={loadingWeather}
            loadingTube={loadingTube}
            balanceFormatted={balanceFormatted}
            balanceLoading={balanceLoading}
          />
        </div>
      )}
      {view === "weather" && (
        <WeatherPage weather={effectiveWeather} loading={loadingWeather} />
      )}
      {view === "tube" && <TubePage tube={effectiveTube} loading={loadingTube} />}
      {view === "transactions" && (
        <TransactionsPage
          balanceFormatted={balanceFormatted}
          balanceLoading={balanceLoading}
        />
      )}
    </div>
  );
};

export default WidgetsDemoView;
